import React, {useRef, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import axios from 'axios';
import {
    Alert,
    Badge,
    Button,
    InputGroup,
    FormControl,
    Form,
    Collapse
} from 'react-bootstrap';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
    faLink,
    faCopy,
    faChartLine,
    faSpinner,
    faLightbulb,
    faKey,
    faTrash,
    faCheck,
    faCog,
    faChevronDown,
    faChevronUp,
    faPen,
    faPlus,
    faBroom,
    faShieldHalved
} from '@fortawesome/free-solid-svg-icons';
import RouteSeo from '../components/RouteSeo';
import ToolCard from '../components/ToolCard';
import {HOME_ROUTE, TOOLS} from '../content/tools';
import PageShell from '../components/layout/PageShell';
import PageHeader from '../components/layout/PageHeader';
import Panel from '../components/layout/Panel';
import {useEndpoints, StoredEndpoint} from '../hooks/useEndpoints';
import {useEndpointStatus} from '../hooks/useEndpointStatus';
import {REQUEST_API_BASE, buildEndpointUrl, slugFromEndpointUrl} from '../utils/publicUrl';
import {ResponseSettingError, isBodylessStatus, responseHeaderRowError, responseHeaderRowsToBag, responseHeadersGroupError, responseSettingError} from '../utils/responseSettings';
import type {ResponseHeaderRow} from '../utils/responseSettings';
import './Home.css';

/**
 * Request base for axios — relative `/api` in production. Saved endpoints and
 * anything copied from this page use `buildEndpointUrl`, which is absolute.
 */
const API_URL = REQUEST_API_BASE;

interface Endpoint {
    url: string;
    slug: string;
}

const Home: React.FC = () => {
    const [generatedEndpoint, setGeneratedEndpoint] = useState<Endpoint | null>(null);
    const [generating, setGenerating] = useState(false);
    const [viewSlug, setViewSlug] = useState('');
    const [copiedField, setCopiedField] = useState<string | null>(null);
    const [showAdvanced, setShowAdvanced] = useState(false);
    /**
     * What the user typed, not a number: a cleared field has to stay cleared
     * rather than silently becoming 200, and the API's rejection of a value is
     * what the field renders underneath itself.
     */
    const [responseStatusCode, setResponseStatusCode] = useState('200');
    /**
     * What the user typed, or `undefined` while the body field is untouched.
     * The two "no text" states are distinct on purpose: an untouched field is
     * sent as nothing and the endpoint keeps its default envelope, while a body
     * the user typed and then cleared is sent as `''` and the endpoint answers
     * zero bytes.
     */
    const [responseBody, setResponseBody] = useState<string | undefined>(undefined);
    const [responseContentType, setResponseContentType] = useState<string>('application/json');
    /**
     * Extra headers the new endpoint answers with, as rows of what the user
     * typed. A row the user added and never filled in is not sent; everything
     * else goes to the API exactly as typed for it to accept or reject.
     */
    const [headerRows, setHeaderRows] = useState<ResponseHeaderRow[]>([]);
    /** The API's rejection of the last generation attempt, if any. */
    const [settingsError, setSettingsError] = useState<ResponseSettingError | null>(null);
    /** The saved row whose label is being edited inline, if any. */
    const [editingId, setEditingId] = useState<string | null>(null);
    const [draftLabel, setDraftLabel] = useState('');
    /**
     * Set while an edit is being abandoned (Escape). Unmounting the input fires
     * a blur, and without this the blur would commit the value Escape just
     * cancelled.
     */
    const cancelledEditRef = useRef(false);
    const navigate = useNavigate();

    // new: endpoints hook
    const {endpoints, addEndpoint, renameEndpoint, removeEndpoint, removeEndpoints, clearEndpoints} = useEndpoints();

    /**
     * Which saved endpoints still exist.
     *
     * The list is browser-local, so the browser is the only thing that knows
     * which slugs to ask about; one request covers all of them when the list
     * loads. `null` means unknown — still in flight, failed, or nothing saved —
     * and unknown is never rendered as expired: a network blip must not label a
     * working endpoint dead.
     */
    const savedSlugs = endpoints.map((ep) => slugFromEndpointUrl(ep.url)).filter(Boolean);
    const endpointStatuses = useEndpointStatus(savedSlugs);

    const statusFor = (slug: string) =>
        (slug && endpointStatuses ? endpointStatuses.get(slug) : undefined);

    /**
     * Only a completed answer that did not find the endpoint counts as
     * expired. A slug the answer says nothing about is unknown, not dead.
     */
    const isExpired = (slug: string) => statusFor(slug)?.exists === false;

    /** The saved rows whose endpoint is gone — what Remove expired drops. */
    const expiredIds = endpoints
        .filter((ep) => isExpired(slugFromEndpointUrl(ep.url)))
        .map((ep) => ep.id);

    // A stored value written before the public-URL fix is a relative path
    // (`/api/endpoint/<slug>`); resolve it against the page origin so the row
    // can still hand an absolute URL to the clipboard.
    const absoluteStoredUrl = (url: string) => {
        if (/^[a-z][a-z0-9+.-]*:\/\//i.test(url)) {
            return url;
        }
        try {
            return new URL(url, window.location.origin).toString();
        } catch (err) {
            return url;
        }
    };

    // One save path for every entry point, so the same endpoint cannot be
    // stored twice under two spellings of its URL (absolute vs legacy relative).
    const saveEndpoint = (url: string, slug?: string) => {
        const key = slug || slugFromEndpointUrl(url);
        if (key && endpoints.some(ep => slugFromEndpointUrl(ep.url) === key)) {
            return;
        }
        addEndpoint(url, key || undefined);
    };

    /**
     * Open the inline label editor for a row. Cancelled is reset here so an
     * Escape in one row cannot swallow the next row's commit.
     */
    const startRename = (id: string, current: string) => {
        cancelledEditRef.current = false;
        setEditingId(id);
        setDraftLabel(current);
    };

    /** Enter / blur: write the draft (empty clears the name) and close the editor. */
    const commitRename = (id: string) => {
        if (cancelledEditRef.current) {
            cancelledEditRef.current = false;
            setEditingId(null);
            return;
        }
        renameEndpoint(id, draftLabel);
        setEditingId(null);
    };

    /** Escape: close without writing, and keep the following blur from committing. */
    const cancelRename = () => {
        cancelledEditRef.current = true;
        setEditingId(null);
    };

    /** Add a header row, edit one in place, or drop one. */
    const addHeaderRow = () => {
        setHeaderRows((rows) => [...rows, {name: '', value: ''}]);
    };

    const updateHeaderRow = (index: number, patch: Partial<ResponseHeaderRow>) => {
        setHeaderRows((rows) =>
            rows.map((row, position) => (position === index ? {...row, ...patch} : row)),
        );
    };

    const removeHeaderRow = (index: number) => {
        setHeaderRows((rows) => rows.filter((_, position) => position !== index));
    };

    /** The rejection that names no single row: too many headers, or a bag over the limit. */
    const headersGroupError = responseHeadersGroupError(settingsError, headerRows);

    /**
     * Create the endpoint.
     *
     * The settings go to the API exactly as typed — a cleared status-code field
     * included — because the API is what decides whether a setting is
     * storable. A rejection is rendered under the field it names (opening the
     * advanced panel so it cannot be hidden behind a collapse) instead of a
     * bare `alert()`.
     */
    const generateEndpoint = async () => {
        setGenerating(true);
        setSettingsError(null);
        try {
            const response = await axios.post(`${API_URL}/generate`, {
                responseStatusCode,
                // Exactly what the field holds: `undefined` for an untouched
                // field (the endpoint keeps its default envelope) and `''` for
                // one the user cleared (the endpoint answers zero bytes).
                responseBody,
                responseContentType,
                // The header rows as a bag, exactly as typed: an unfilled row
                // adds nothing, and an empty list means no configured headers.
                responseHeaders: responseHeaderRowsToBag(headerRows),
            });
            setGeneratedEndpoint(response.data);

            // persist to local storage
            if (response.data && response.data.url) {
                // label is optional; use slug as label when available
                const slug = response.data.slug || slugFromEndpointUrl(response.data.url);
                saveEndpoint(slug ? buildEndpointUrl(slug) : response.data.url, slug || undefined);

                // Navigate to the endpoint view
                navigate(`/endpoint/${response.data.slug}`);
            }
        } catch (error) {
            console.error('Error generating endpoint:', error);
            const failure = responseSettingError(error, 'Failed to generate endpoint');
            setSettingsError(failure);
            if (failure.field !== null) {
                setShowAdvanced(true);
            }
        } finally {
            setGenerating(false);
        }
    };

    const copyToClipboard = async (text: string, fieldName: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedField(fieldName);
            setTimeout(() => setCopiedField(null), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    // Single entry point for both the row click and the keyboard/row link.
    const openEndpoint = (slug: string) => {
        if (!slug) {
            return;
        }
        // Save to local storage (deduplicated by slug)
        saveEndpoint(buildEndpointUrl(slug), slug);
        navigate(`/endpoint/${slug}`);
    };

    const viewEndpoint = () => {
        openEndpoint(viewSlug.trim());
    };

    return (
        <>
            <RouteSeo route={HOME_ROUTE} />
            <PageShell width="wide">
                <PageHeader
                    className="home-hero"
                    align="center"
                    icon={faKey}
                    title="What Am I Sending"
                    subtitle="Capture, inspect, and debug HTTP requests with instant endpoint generation — built for webhook testing, API debugging and request inspection."
                    meta={
                        <>
                            <span className="home-hero__badge">
                                <FontAwesomeIcon icon={faCheck} aria-hidden="true"/>
                                No signup
                            </span>
                            <span className="home-hero__badge">
                                <FontAwesomeIcon icon={faShieldHalved} aria-hidden="true"/>
                                {TOOLS.length} client-side tools
                            </span>
                            <span className="home-hero__badge">
                                <FontAwesomeIcon icon={faTrash} aria-hidden="true"/>
                                Requests deleted after 5 days
                            </span>
                        </>
                    }
                    actions={
                        <>
                            <Button
                                variant="primary"
                                size="lg"
                                onClick={generateEndpoint}
                                disabled={generating}
                            >
                                {generating ? (
                                    <>
                                        <FontAwesomeIcon icon={faSpinner} spin className="me-2"/>
                                        Generating...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faLink} className="me-2"/>
                                        Generate URL
                                    </>
                                )}
                            </Button>
                            <Button
                                variant="outline-secondary"
                                onClick={() => setShowAdvanced(!showAdvanced)}
                                aria-expanded={showAdvanced}
                                aria-controls="advanced-settings"
                            >
                                <FontAwesomeIcon icon={showAdvanced ? faChevronUp : faChevronDown} className="me-2"/>
                                {showAdvanced ? 'Hide' : 'Show'} Advanced Settings
                            </Button>
                        </>
                    }
                />

                <div className="home-start">
                    <Panel className="home-start__primary">
                        <h3 id="home-generate-heading" className="home-start__title">
                            <FontAwesomeIcon icon={faKey} className="me-2"/>
                            Generate New Endpoint
                        </h3>
                        <p className="text-muted mb-3">
                            Create a unique URL to receive and capture HTTP requests, then send anything at it.
                        </p>

                        <Collapse in={showAdvanced}>
                            <div id="advanced-settings" className="home-start__advanced">
                                <h4 className="home-start__subtitle">
                                    <FontAwesomeIcon icon={faCog} className="me-2"/>
                                    Response Settings
                                </h4>
                                <Form>
                                    <Form.Group className="mb-3">
                                        <Form.Label><strong>Response Status Code</strong></Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={responseStatusCode}
                                            onChange={(e) => setResponseStatusCode(e.target.value)}
                                            placeholder="200"
                                            min="200"
                                            max="999"
                                            isInvalid={settingsError?.field === 'responseStatusCode'}
                                        />
                                        {settingsError?.field === 'responseStatusCode' ? (
                                            <div className="invalid-feedback d-block" role="alert">
                                                {settingsError.message}
                                            </div>
                                        ) : (
                                            <Form.Text className="text-muted">
                                                HTTP status code to return (e.g., 200, 201, 400, 404, 500)
                                            </Form.Text>
                                        )}
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label><strong>Content Type</strong></Form.Label>
                                        <Form.Select
                                            value={responseContentType}
                                            onChange={(e) => setResponseContentType(e.target.value)}
                                        >
                                            <option value="application/json">application/json</option>
                                            <option value="text/plain">text/plain</option>
                                            <option value="text/html">text/html</option>
                                            <option value="application/xml">application/xml</option>
                                        </Form.Select>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label><strong>Response Headers</strong></Form.Label>
                                        {headerRows.length === 0 ? (
                                            <Form.Text className="text-muted d-block mb-2">
                                                No extra headers. A header list sent to a capture does not
                                                configure this: the endpoint answers with its Content Type alone.
                                            </Form.Text>
                                        ) : null}
                                        {headerRows.map((row, index) => {
                                            const rowError = responseHeaderRowError(settingsError, row.name);

                                            return (
                                                <div className="settings-header-row" key={index}>
                                                    <div className="settings-header-row__fields">
                                                        <Form.Control
                                                            type="text"
                                                            value={row.name}
                                                            placeholder="X-Trace-Id"
                                                            aria-label={`Header ${index + 1} name`}
                                                            onChange={(e) => updateHeaderRow(index, {name: e.target.value})}
                                                            isInvalid={rowError !== null}
                                                        />
                                                        <Form.Control
                                                            type="text"
                                                            value={row.value}
                                                            placeholder="value"
                                                            aria-label={`Header ${index + 1} value`}
                                                            onChange={(e) => updateHeaderRow(index, {value: e.target.value})}
                                                            isInvalid={rowError !== null}
                                                        />
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            aria-label={`Remove header ${index + 1}`}
                                                            onClick={() => removeHeaderRow(index)}
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} aria-hidden="true"/>
                                                        </Button>
                                                    </div>
                                                    {rowError ? (
                                                        <div className="invalid-feedback d-block" role="alert">
                                                            {rowError}
                                                        </div>
                                                    ) : null}
                                                </div>
                                            );
                                        })}
                                        {/* A rejection of the list itself — too many
                                            headers, or a bag over the size limit —
                                            belongs to no single row. */}
                                        {headersGroupError ? (
                                            <div className="invalid-feedback d-block" role="alert">
                                                {headersGroupError}
                                            </div>
                                        ) : null}
                                        <Button variant="outline-secondary" size="sm" onClick={addHeaderRow}>
                                            <FontAwesomeIcon icon={faPlus} className="me-2" aria-hidden="true"/>
                                            Add header
                                        </Button>
                                    </Form.Group>

                                    <Form.Group className="mb-0">
                                        <Form.Label><strong>Response Body</strong></Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={5}
                                            value={responseBody ?? ''}
                                            onChange={(e) => setResponseBody(e.target.value)}
                                            placeholder={responseContentType === 'application/json'
                                                ? '{"message": "Success", "data": {...}}'
                                                : 'Response body content'}
                                            className="font-monospace"
                                        />
                                        <Form.Text className="text-muted">
                                            Leave empty for the default response; clear the field after
                                            typing to return no body. JSON is re-serialised, so it is
                                            always valid JSON on the wire.
                                        </Form.Text>
                                        {isBodylessStatus(responseStatusCode) ? (
                                            <Form.Text className="text-warning d-block" role="status">
                                                A {responseStatusCode.trim()} response carries no body, so
                                                this will not be sent.
                                            </Form.Text>
                                        ) : null}
                                    </Form.Group>
                                </Form>
                            </div>
                        </Collapse>

                        {/* A rejection the API tied to neither the status field nor
                            the header list. The content type and the body have no
                            error slot of their own in this form, and the header
                            rows render their own rejection beside themselves. */}
                        {settingsError && settingsError.field !== 'responseStatusCode' && settingsError.field !== 'responseHeaders' ? (
                            <Alert variant="danger" className="py-2" role="alert">
                                {settingsError.message}
                            </Alert>
                        ) : null}

                        {generatedEndpoint && (
                            <div className="home-result">
                                <p className="fw-bold mb-2">Your unique endpoint:</p>
                                <InputGroup className="mb-3">
                                    <FormControl
                                        readOnly
                                        value={generatedEndpoint.url}
                                        className="bg-light"
                                        aria-label="Generated endpoint URL"
                                    />
                                    <Button
                                        variant="success"
                                        onClick={() => copyToClipboard(generatedEndpoint.url, 'generatedUrl')}
                                        id="copy-button"
                                        aria-label="Copy generated endpoint URL"
                                    >
                                        <FontAwesomeIcon icon={copiedField === 'generatedUrl' ? faCheck : faCopy}/>
                                        {copiedField === 'generatedUrl' && ' Copied!'}
                                    </Button>
                                </InputGroup>
                                <Button
                                    variant="secondary"
                                    onClick={() => navigate(`/endpoint/${generatedEndpoint.slug}`)}
                                >
                                    <FontAwesomeIcon icon={faChartLine} className="me-2"/>
                                    View Requests
                                </Button>
                            </div>
                        )}
                    </Panel>

                    <Panel variant="quiet" className="home-start__secondary">
                        <h3 className="home-start__title">
                            <FontAwesomeIcon icon={faChartLine} className="me-2"/>
                            View Existing Endpoint
                        </h3>
                        <p className="text-muted mb-3">
                            Already have a slug? Jump straight to the requests it captured.
                        </p>
                        <InputGroup>
                            <FormControl
                                type="text"
                                value={viewSlug}
                                onChange={(e) => setViewSlug(e.target.value)}
                                placeholder="Enter endpoint slug..."
                                aria-label="Endpoint slug"
                                onKeyDown={(e) => {
                                    // Call viewEndpoint when the user presses Enter
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        viewEndpoint();
                                    }
                                }}
                            />
                            <Button
                                variant="outline-primary"
                                onClick={viewEndpoint}
                                disabled={!viewSlug.trim()}
                            >
                                View
                            </Button>
                        </InputGroup>
                    </Panel>
                </div>

                <Panel
                    id="saved-endpoints"
                    className="home-saved"
                    title="Saved Endpoints"
                    subtitle="Endpoints you generated are saved locally in your browser for easy access."
                    actions={endpoints.length > 0 ? (
                        <>
                            {/* Only when there is something to remove: the control
                                is the answer to the Expired badges, and drops
                                nothing else. */}
                            {expiredIds.length > 0 ? (
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    className="me-2"
                                    onClick={() => removeEndpoints(expiredIds)}
                                    aria-label="Remove expired endpoints from this browser's list"
                                    title="Remove the saved endpoints that no longer exist, keeping the live ones"
                                >
                                    <FontAwesomeIcon icon={faBroom} className="me-1"/>
                                    Remove expired
                                </Button>
                            ) : null}
                            <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => clearEndpoints()}
                                aria-label="Clear all saved endpoints from this browser's list"
                                title="Clear all saved endpoints from this browser's list"
                            >
                                <FontAwesomeIcon icon={faTrash} className="me-1"/>
                                Clear List
                            </Button>
                        </>
                    ) : undefined}
                >
                    {endpoints.length === 0 ? (
                        <div className="endpoint-list-empty">
                            <FontAwesomeIcon icon={faKey} aria-hidden="true"/>
                            <p className="mb-0">
                                No saved endpoints yet. Generate one above and it will show up here.
                            </p>
                        </div>
                    ) : (
                        <div className="endpoint-table-wrap">
                            <table className="table align-middle endpoint-table mb-0">
                                <thead>
                                <tr>
                                    <th scope="col">Label</th>
                                    <th scope="col">Slug</th>
                                    <th scope="col" className="endpoint-table__requests">Requests</th>
                                    <th scope="col" className="endpoint-table__created">Created</th>
                                    <th scope="col" className="text-end">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {endpoints.map((ep: StoredEndpoint) => {
                                    const slug = slugFromEndpointUrl(ep.url);
                                    // The name a human chose, else the slug — so an
                                    // un-renamed row looks exactly as it did before.
                                    const name = ep.label || slug || ep.url;
                                    const isEditing = editingId === ep.id;
                                    const status = statusFor(slug);
                                    // A completed answer that did not find the
                                    // endpoint. Unknown (no status) is not this.
                                    const expired = status?.exists === false;
                                    return (
                                        <tr key={ep.id}>
                                            <td className="endpoint-table__label">
                                                {isEditing ? (
                                                    <FormControl
                                                        size="sm"
                                                        className="endpoint-label-input"
                                                        value={draftLabel}
                                                        aria-label={`New name for ${name}`}
                                                        placeholder={slug || ep.url}
                                                        autoFocus
                                                        onChange={(e) => setDraftLabel(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                commitRename(ep.id);
                                                            } else if (e.key === 'Escape') {
                                                                e.preventDefault();
                                                                cancelRename();
                                                            }
                                                        }}
                                                        onBlur={() => commitRename(ep.id)}
                                                    />
                                                ) : slug && !expired ? (
                                                    <Link
                                                        to={`/endpoint/${slug}`}
                                                        onClick={() => openEndpoint(slug)}
                                                    >
                                                        {name}
                                                    </Link>
                                                ) : (
                                                    /* Expired rows are plain text on
                                                       purpose: the link would land on
                                                       the inspector's "Endpoint not
                                                       found" alert. */
                                                    <span className={expired ? 'endpoint-table__name--expired' : undefined}>
                                                        {name}
                                                    </span>
                                                )}
                                                {expired ? (
                                                    /* `bg=""` on purpose: Badge defaults to
                                                       `bg="primary"`, and every bootstrap
                                                       `bg-*` utility is `!important`, so either
                                                       one would paint over the muted tokens in
                                                       Home.css. An empty value emits no utility
                                                       class and leaves the badge styled by its
                                                       own class alone (pinned in Home.test). */
                                                    <Badge
                                                        bg=""
                                                        className="endpoint-badge--expired"
                                                        title="This endpoint no longer exists"
                                                    >
                                                        Expired
                                                    </Badge>
                                                ) : null}
                                            </td>
                                            <td className="endpoint-slug">{slug || ep.url}</td>
                                            <td className="endpoint-meta endpoint-table__requests">
                                                {status && status.exists ? (
                                                    <span className="endpoint-request-count">
                                                        {status.requestCount}
                                                    </span>
                                                ) : (
                                                    /* Unknown (no answer yet) reads
                                                       the same as expired here:
                                                       neither has a count to show. */
                                                    <span
                                                        className="endpoint-request-count endpoint-request-count--unknown"
                                                    >
                                                        &mdash;
                                                    </span>
                                                )}
                                            </td>
                                            <td className="endpoint-meta endpoint-table__created">
                                                {new Date(ep.createdAt).toLocaleString()}
                                            </td>
                                            <td className="text-end text-nowrap">
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    className="me-2"
                                                    aria-label={`Rename ${name}`}
                                                    title={`Rename ${name}`}
                                                    disabled={isEditing}
                                                    onClick={() => startRename(ep.id, ep.label || '')}
                                                >
                                                    <FontAwesomeIcon icon={faPen}/>
                                                </Button>
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    className="me-2"
                                                    aria-label={`Copy URL for ${name}`}
                                                    title={expired
                                                        ? `${name} has expired, so there is no live URL to copy`
                                                        : `Copy URL for ${name}`}
                                                    disabled={expired}
                                                    onClick={() => copyToClipboard(
                                                        slug ? buildEndpointUrl(slug) : absoluteStoredUrl(ep.url),
                                                        `saved-${ep.id}`,
                                                    )}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={copiedField === `saved-${ep.id}` ? faCheck : faCopy}/>
                                                </Button>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    aria-label={`Remove ${name} from this browser's list`}
                                                    title={`Remove ${name} from this browser's list`}
                                                    onClick={() => removeEndpoint(ep.id)}
                                                >
                                                    <FontAwesomeIcon icon={faTrash}/>
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Panel>

                <section className="home-tools" aria-labelledby="home-tools-heading">
                    <h2 className="home-tools__heading" id="home-tools-heading">
                        Free developer tools
                    </h2>
                    <p className="home-tools__lede">
                        Every tool runs entirely in your browser — nothing you paste is uploaded.
                    </p>
                    <ul className="home-tools__list tool-grid">
                        {TOOLS.map((tool) => (
                            <li key={tool.slug}>
                                <ToolCard route={tool} headingLevel={3}/>
                            </li>
                        ))}
                    </ul>
                    <p className="home-tools__more">
                        <Link to="/tools">Browse all developer tools</Link>
                    </p>
                </section>

                <div className="home-notes">
                    <Panel variant="quiet" title="How to Use" icon={faLightbulb}>
                        <ol className="mb-0">
                            <li>Click "Generate URL" to create a unique endpoint</li>
                            <li>Send HTTP requests to the generated URL (GET, POST, PUT, etc.)</li>
                            <li>View all captured requests with headers, body, and query parameters</li>
                            <li>Use this for webhook testing, API debugging, and request inspection</li>
                        </ol>
                    </Panel>

                    <Panel variant="quiet" title="Data Retention Policy" icon={faShieldHalved}>
                        <p className="mb-0">
                            Captured requests are automatically deleted after <strong>5 days</strong>, and an endpoint
                            is deleted once it has been idle for <strong>5 days</strong> — to ensure privacy and manage
                            storage.
                        </p>
                    </Panel>
                </div>
            </PageShell>
        </>
    );
};

export default Home;
